import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "alohi_admin_token";

const STATUS_OPTIONS = ["pending", "paid", "shipped", "cancelled"];

// Network / HTTP constants
const HTTP_UNAUTHORIZED = 401;
const LOGIN_TIMEOUT_MS = 8000;
const ORDERS_FETCH_TIMEOUT_MS = 10000;

// Use sessionStorage (cleared on tab close, not shared cross-tab) instead of
// localStorage to reduce XSS/persistence risk for the admin token.
const tokenStore = {
  get: () => {
    try { return sessionStorage.getItem(STORAGE_KEY) || ""; } catch { return ""; }
  },
  set: (t) => {
    try { sessionStorage.setItem(STORAGE_KEY, t); } catch { /* ignore */ }
  },
  clear: () => {
    try { sessionStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
    // also clear any legacy localStorage value
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
  },
};

const StatusBadge = ({ status }) => (
  <span className={`admin-status admin-status-${status}`}>{status}</span>
);

const OrderRow = ({ order }) => (
  <details className="admin-order-row" data-testid={`admin-order-${order.order_number}`}>
    <summary>
      <span className="admin-cell admin-order-num">{order.order_number}</span>
      <span className="admin-cell admin-customer">{order.customer_name}</span>
      <span className="admin-cell admin-product">
        {order.product_name} × {order.quantity}
      </span>
      <span className="admin-cell admin-total">${order.total.toFixed(2)}</span>
      <span className="admin-cell"><StatusBadge status={order.status} /></span>
      <span className="admin-cell admin-date">
        {new Date(order.created_at).toLocaleDateString()}
      </span>
    </summary>
    <div className="admin-order-detail">
      <div className="admin-detail-grid">
        <div>
          <h4>Customer</h4>
          <p>{order.customer_name}</p>
          <p><a href={`mailto:${order.email}`}>{order.email}</a></p>
          <p><a href={`tel:${order.phone}`}>{order.phone}</a></p>
        </div>
        <div>
          <h4>Shipping address</h4>
          <p>{order.address_line1}</p>
          {order.address_line2 ? <p>{order.address_line2}</p> : null}
          <p>{order.city}, {order.state} {order.postal_code}</p>
          <p>{order.country}</p>
        </div>
        <div>
          <h4>Order</h4>
          <p>Subtotal: ${order.subtotal.toFixed(2)}</p>
          <p>Shipping: ${order.shipping_fee.toFixed(2)}</p>
          <p><strong>Total: ${order.total.toFixed(2)}</strong></p>
          <p>Est. delivery: {order.estimated_delivery_from} → {order.estimated_delivery_to}</p>
        </div>
        {order.notes ? (
          <div>
            <h4>Notes</h4>
            <p>{order.notes}</p>
          </div>
        ) : null}
      </div>
    </div>
  </details>
);

const LoginGate = ({ onLogin }) => {
  const [token, setToken] = useState("");
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await axios.get(`${API}/orders/admin`, {
        headers: { "X-Admin-Token": token },
        timeout: LOGIN_TIMEOUT_MS,
      });
      tokenStore.set(token);
      onLogin(token);
    } catch (err) {
      const status = err?.response?.status;
      setError(status === HTTP_UNAUTHORIZED ? "Incorrect access token." : "Could not reach server.");
    }
  };

  return (
    <div className="admin-login-shell">
      <div className="admin-login-card">
        <p className="admin-eyebrow">Admin · Orders</p>
        <h1 className="admin-h1">'ALOHI Orders</h1>
        <p className="admin-sub">Enter the admin access token to view incoming orders.</p>
        <form onSubmit={submit} className="admin-login-form">
          <input
            type="password"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            placeholder="Admin access token"
            data-testid="admin-token-input"
            autoFocus
          />
          <button type="submit" data-testid="admin-login-submit">Sign in</button>
          {error ? <p className="admin-error">{error}</p> : null}
        </form>
      </div>
    </div>
  );
};

const AdminOrdersPage = () => {
  const [token, setToken] = useState(() => tokenStore.get());
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async (t) => {
    if (!t) return;
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API}/orders/admin`, {
        headers: { "X-Admin-Token": t },
        timeout: ORDERS_FETCH_TIMEOUT_MS,
      });
      setOrders(res.data || []);
    } catch (err) {
      const status = err?.response?.status;
      if (status === HTTP_UNAUTHORIZED) {
        tokenStore.clear();
        setToken("");
        setError("Session expired. Please sign in again.");
      } else {
        setError("Could not load orders.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (token) load(token);
  }, [token, load]);

  const logout = () => {
    tokenStore.clear();
    setToken("");
    setOrders([]);
  };

  if (!token) return <LoginGate onLogin={setToken} />;

  const totalRevenue = orders.reduce((sum, o) => sum + (o.total || 0), 0);
  const pending = orders.filter((o) => o.status === "pending").length;

  return (
    <div className="admin-shell" data-testid="admin-orders-page">
      <header className="admin-header">
        <div>
          <p className="admin-eyebrow">Admin · Orders</p>
          <h1 className="admin-h1">Incoming Orders</h1>
        </div>
        <div className="admin-header-actions">
          <button onClick={() => load(token)} className="admin-btn-ghost" data-testid="admin-refresh">
            Refresh
          </button>
          <button onClick={logout} className="admin-btn-ghost" data-testid="admin-logout">
            Sign out
          </button>
        </div>
      </header>

      <section className="admin-stats">
        <div className="admin-stat">
          <span className="admin-stat-label">Total orders</span>
          <span className="admin-stat-value">{orders.length}</span>
        </div>
        <div className="admin-stat">
          <span className="admin-stat-label">Pending</span>
          <span className="admin-stat-value">{pending}</span>
        </div>
        <div className="admin-stat">
          <span className="admin-stat-label">Revenue</span>
          <span className="admin-stat-value">${totalRevenue.toFixed(2)}</span>
        </div>
      </section>

      {error ? <p className="admin-error">{error}</p> : null}

      <OrdersBody loading={loading} orders={orders} />

      <p className="admin-note">
        Status options: {STATUS_OPTIONS.join(" · ")} (manual update via Mongo for now).
      </p>
    </div>
  );
};

const OrdersBody = ({ loading, orders }) => {
  if (loading) {
    return <p className="admin-empty">Loading orders…</p>;
  }
  if (orders.length === 0) {
    return <p className="admin-empty">No orders yet. New Buy Now submissions will appear here.</p>;
  }
  return (
    <div className="admin-table">
      <div className="admin-table-head">
        <span>Order #</span>
        <span>Customer</span>
        <span>Product</span>
        <span>Total</span>
        <span>Status</span>
        <span>Date</span>
      </div>
      {orders.map((o) => (
        <OrderRow key={o.id} order={o} />
      ))}
    </div>
  );
};

export default AdminOrdersPage;
